import { useCallback, useState } from 'react';
import { selectSite } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { showToast } from '@shared/services/toast';
import { clearReviewOrderCopy } from '@shared/utils/reviewOrderStorage';

/**
 * In-app "switch site" within the ACTIVE customer, used by the nested site rows of the sidebar
 * `FirmSelector`. Unlike `useCompanySwitcher` there is no firm change, so no subdomain hand-off and no
 * firm rollback: it sets the backend active-site cookie via `selectSite` and reloads the same host so ALL
 * page data (roles, sidebar visibility, subscriptions, users…) is cleared and re-hydrated for the new
 * site. Switching to a site of ANOTHER customer goes through `useCompanySwitcher.switchCompany(company,
 * site)` instead. Failures surface via `showToast.error` and re-enable the control.
 *
 * @returns {{
 *   switching: boolean,
 *   switchSite: (site: {haloPsaSiteId:number, isCurrent?:boolean}) => Promise<void>
 * }}
 */
export function useSiteSwitcher() {
  const [switching, setSwitching] = useState(false);
  const setSelectedSite = useAuthStore((s) => s.setSelectedSite);

  const switchSite = useCallback(
    async (site) => {
      // Ignore a click on the already-active site, an invalid entry, or a switch already in flight.
      if (!site || site.isCurrent || switching) return;

      setSwitching(true);
      try {
        await selectSite(site.haloPsaSiteId);
        // Keep the persisted copy consistent so the stores reflect the new site after the navigation.
        setSelectedSite(site);
        // An order started on the previous site must not be revived on this one.
        clearReviewOrderCopy();
        // Navigate to the home route rather than reloading in place. A full navigation still re-scopes
        // every in-memory store to the new site, and landing on "/" lets HomeRedirect send the user to
        // the page this site's lifecycle allows — dashboard for an established site, or checkout for a
        // site with no invoices yet. An in-place reload would land back on the current URL, which may be
        // gated off for the new site (e.g. /dashboard) and render blank.
        globalThis.location.href = '/';
      } catch (e) {
        console.error('[Site Switch] Failed to switch site:', e);
        showToast.error(e?.response?.data?.message || e?.message || 'Failed to switch site. Please try again.');
        setSwitching(false);
      }
    },
    [switching, setSelectedSite],
  );

  return { switching, switchSite };
}
